import { create } from 'zustand'
import { Group, CreateGroupDto, UpdateGroupDto } from '@/types'
import { groupsService } from '@/services/groups.service'
import { useClientsStore } from './clients.store'

interface GroupsState {
  groups: Group[] | null
  isInitialized: boolean
  fetchGroups: () => Promise<void>
  addGroup: (group: CreateGroupDto) => Promise<Group>
  updateGroup: (id: string, group: UpdateGroupDto) => Promise<Group>
  deleteGroup: (id: string) => Promise<void>
  getGroupById: (id: string) => Group | undefined
}

export const useGroupsStore = create<GroupsState>((set, get) => ({
  groups: null,
  isInitialized: false,

  fetchGroups: async () => {
    // Only fetch if not already initialized
    if (get().isInitialized) {
      return
    }

    try {
      const data = await groupsService.getAll()
      set({ groups: data, isInitialized: true })
    } catch (error) {
      console.error('Error loading groups:', error)
      throw error
    }
  },

  addGroup: async (groupData: CreateGroupDto) => {
    const newGroup = await groupsService.create(groupData)
    set(state => ({
      groups: [...state.groups || [], newGroup],
    }))
    return newGroup
  },

  updateGroup: async (id: string, groupData: UpdateGroupDto) => {
    const updatedGroup = await groupsService.update(id, groupData)
    set(state => ({
      groups: state.groups?.map(group => (group._id === id ? updatedGroup : group)),
    }))

    // Update all clients that have this group
    const clientsStore = useClientsStore.getState()
    const clients = clientsStore.clients
    const clientsWithThisGroup = clients?.filter(client => {
      const clientGroupId = typeof client.group === 'string' ? client.group : client.group._id
      return clientGroupId === id
    })

    if (clientsWithThisGroup && clientsWithThisGroup.length > 0) {
      useClientsStore.setState(state => ({
        clients: state.clients?.map(client => {
          const clientGroupId = typeof client.group === 'string' ? client.group : client.group._id
          if (clientGroupId === id) {
            // Update the group reference in the client
            return {
              ...client,
              group: updatedGroup,
            }
          }
          return client
        }),
      }))
    }

    return updatedGroup
  },

  deleteGroup: async (id: string) => {
    await groupsService.delete(id)
    set(state => ({
      groups: state.groups?.filter(group => group._id !== id),
    }))
  },

  getGroupById: (id: string) => {
    return get().groups?.find(group => group._id === id)
  },
}))
